import { useBridgeContext } from '@sendible/shared-state-bridge';
import { useMemo } from 'react';
import { fetchWithHeaders, reportErrorToFaro } from '@sendible/common';
import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToastNotificationContext } from '@sendible/design-system';
import { NotificationTypeEnum } from '@sendible/design-system/src/components/molecules/Toast';
import { useTranslation } from 'react-i18next';
import endpoints from '../../data-layer/endpoints';
import { useInfiniteQueryWithAccessToken, useQueryWithAccessToken } from '../../data-layer/useReactQueryWithAccessToken';
import { getMonthRange, getWeekRange } from '../../pages/Calendar/utils';
import { POSTS_PER_DAY_LIMITS, useCalendarContext } from '../../pages/Calendar/context';
import { useSelectionContext } from '../../pages/Calendar/selectionContext';

type CalendarSummaryDay = {
  date: string;
  messageCount: number;
  messages: CalendarMessage[];
};

type RescheduleMessageDataWithLocalized = RescheduleMessageData & {
  newSendDate: CalendarMessage['sendDate'];
  newSendDateLocalized?: CalendarMessage['sendDateLocalized'];
};

export const COMPACT_MESSAGES_MODIFIER = 3;
export const PAGE_SIZE_DEFAULT = 10;
export const PAGE_SIZE_MAX = 100;
export const DAY_INIT_TIME = '00:00:00';
export const DAY_END_TIME = '23:59:59';

const { GetCalendarMessage, GetCalendar, GetCalendarSummary, GetHolidays, RescheduleMessage, ResolveRecipients, DeleteMessage } = endpoints;

const getWorkspaceEndpoint = (endpoint: string, isWorkspaceMode: boolean) => (isWorkspaceMode ? endpoint.replace('/v1.0/', '/v2.0/') : endpoint);
const getWorkspaceParams = (userId: number | undefined, isWorkspaceMode: boolean) => (isWorkspaceMode && userId ? { workspaceId: userId } : {});
const getDashboardParams = (userId: number | undefined, isWorkspaceMode: boolean) => (!isWorkspaceMode && userId ? { dashboardId: userId } : {});

const buildDateTime = (date: CalendarMonthString, time = DAY_INIT_TIME) => `${date} ${time}`;

export type FetchAllMessageIdsParams = {
  accessToken: string;
  userId: number;
  email: string;
  endISO: string;
  filterParams?: Record<string, string>;
  isSwitchedUser?: boolean;
  isWorkspaceMode: boolean;
  signal?: AbortSignal;
  startISO: string;
  username?: string;
};

// Dedicated to "select all": id-only, workspace/dashboard-scoped the same way the grid's own
// summary/message queries are (getWorkspaceEndpoint/getWorkspaceParams/getDashboardParams) —
// deliberately not the exportUtils.ts CSV-export helper, which hard-codes dashboardId/v1.0 and
// fetches full message payloads it doesn't need here. Processing posts are excluded, matching
// their checkbox being disabled on the cards themselves.
export async function fetchAllMessageIdsForRange({
  accessToken,
  userId,
  email,
  endISO,
  filterParams,
  isSwitchedUser,
  isWorkspaceMode,
  signal,
  startISO,
  username,
}: FetchAllMessageIdsParams): Promise<CalendarMessage['id'][]> {
  const auth = isSwitchedUser ? { username, logged_in_access_token: accessToken } : { access_token: accessToken };
  const endpoint = getWorkspaceEndpoint(GetCalendar.endpoint, isWorkspaceMode);
  const sharedParams = {
    ...auth,
    username: email,
    from: `${startISO} 00:00:00`,
    to: `${endISO} 23:59:59`,
    ...getDashboardParams(userId, isWorkspaceMode),
    ...getWorkspaceParams(userId, isWorkspaceMode),
    ...filterParams,
  };

  const perPage = 100;
  const ids: CalendarMessage['id'][] = [];
  let page = 1;
  let hasMore = true;

  while (hasMore) {
    const response = await fetchWithHeaders({
      method: 'GET',
      url: endpoint,
      params: { ...sharedParams, page, pageSize: perPage, perPage },
      signal,
    });
    const payload = response as { result?: CalendarMessage[] } | CalendarMessage[];
    const result: CalendarMessage[] = Array.isArray(payload) ? payload : (payload.result ?? []);

    if (!Array.isArray(result) || result.length === 0) {
      hasMore = false;
    } else {
      result.forEach((message) => {
        if (message?.id != null && message.status !== 'Processing') ids.push(message.id);
      });
      hasMore = result.length >= perPage;
      page += 1;
    }
  }

  return ids;
}

export const optimisticRescheduleSummary = (data: CalendarSummaryDay[], payload: RescheduleMessageDataWithLocalized): CalendarSummaryDay[] => {
  const oldDate = payload.message.sendDateLocalized.slice(0, 10);

  const effectiveLocalized = payload.newSendDateLocalized ?? payload.newSendDate;
  const newDate = effectiveLocalized.slice(0, 10);

  const newMessage: CalendarMessage = {
    ...payload.message,
    sendDate: payload.newSendDate,
    sendDateLocalized: effectiveLocalized,
  };

  let modified = data.map((d) => {
    if (d.date !== oldDate) return d;

    const filtered = d.messages.filter((m) => m.id !== payload.message.id);

    return {
      ...d,
      messages: filtered,
      // `messages` only holds the posts fetched for the cell, not every post of the day, so count from the day's total.
      messageCount: Math.max(0, d.messageCount - 1),
    };
  });

  modified = modified.filter((d) => d.messageCount > 0 || d.date !== oldDate);

  const target = modified.find((d) => d.date === newDate);

  if (target) {
    const withoutDup = target.messages.filter((m) => m.id !== newMessage.id);
    const alreadyCounted = withoutDup.length !== target.messages.length;

    // Keep the day in scheduled-time order, as the API returns it.
    target.messages = [...withoutDup, newMessage].sort((a, b) =>
      a.sendDateLocalized < b.sendDateLocalized ? -1 : a.sendDateLocalized > b.sendDateLocalized ? 1 : 0
    );
    target.messageCount = alreadyCounted ? target.messageCount : target.messageCount + 1;
  } else {
    modified.push({
      date: newDate,
      messageCount: 1,
      messages: [newMessage],
    });
  }

  modified.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  return modified;
};

export const useGetHolidays = (
  date: CalendarMonthString,
  userWeekStartDay: UserWeekStartDay,
  options?: { range?: HolidaysRangeMode; enabled?: boolean }
) => {
  const range = options?.range ?? 'month';

  const [from, to] = useMemo<[CalendarMonthString, CalendarMonthString]>(() => {
    if (!date) return ['' as CalendarMonthString, '' as CalendarMonthString];

    if (range === 'day') return [date, date];

    return getMonthRange(date, userWeekStartDay);
  }, [date, userWeekStartDay, range]);

  return useQueryWithAccessToken<Holiday[]>(
    [
      `${GetHolidays.endpoint}`,
      {
        from,
        to,
      },
    ],
    {
      refetchInterval: GetHolidays.interval,
      enabled: !!date && !!options?.enabled,
      cacheTime: GetHolidays.cacheTime,
      useErrorBoundary: true,
    }
  );
};
// ..

export const useGetMessage = (messageId?: CalendarMessage['id']) => {
  const [{ user }] = useBridgeContext();
  const { userId } = user || {};
  const isWorkspaceMode = Boolean(user?.isWorkspaceMode);
  const endpoint = getWorkspaceEndpoint(GetCalendarMessage.endpoint, isWorkspaceMode);

  return useQueryWithAccessToken<CalendarMessage>(
    [
      `${endpoint}/${messageId}`,
      {
        ...getDashboardParams(userId, isWorkspaceMode),
        ...getWorkspaceParams(userId, isWorkspaceMode),
      },
    ],
    {
      enabled: !!messageId && !!userId,
      useErrorBoundary: true,
    }
  );
};

export const useGetSummaryMessagesByMonth = (date: CalendarMonthString, userWeekStartDay: UserWeekStartDay) => {
  const [
    {
      user: { email, userId, isWorkspaceMode },
    },
  ] = useBridgeContext();
  const endpoint = getWorkspaceEndpoint(GetCalendarSummary.endpoint, Boolean(isWorkspaceMode));
  const { filterParams, density } = useCalendarContext();

  const [fromDay, toDay] = useMemo(() => getMonthRange(date, userWeekStartDay), [date, userWeekStartDay]);
  const from = buildDateTime(fromDay);
  const to = buildDateTime(toDay, DAY_END_TIME);

  return useQueryWithAccessToken<CalendarData[]>(
    [
      `${endpoint}`,
      {
        username: email,
        from,
        to,
        messagesPerDay: POSTS_PER_DAY_LIMITS[density].max,
        ...getDashboardParams(userId, Boolean(isWorkspaceMode)),
        ...getWorkspaceParams(userId, Boolean(isWorkspaceMode)),
        ...filterParams,
      },
    ],
    {
      refetchInterval: GetCalendar.interval,
      enabled: !!userId && !!date,
      cacheTime: GetCalendar.cacheTime,
      useErrorBoundary: true,
    }
  );
};

export const useGetSummaryMessagesByWeek = (date: CalendarMonthString, userWeekStartDay: UserWeekStartDay) => {
  const [
    {
      user: { email, userId, isWorkspaceMode },
    },
  ] = useBridgeContext();
  const endpoint = getWorkspaceEndpoint(GetCalendarSummary.endpoint, Boolean(isWorkspaceMode));
  const { filterParams, density } = useCalendarContext();

  const [fromDay, toDay] = useMemo(() => getWeekRange(date, userWeekStartDay), [date, userWeekStartDay]);
  const from = buildDateTime(fromDay);
  const to = buildDateTime(toDay, DAY_END_TIME);

  return useQueryWithAccessToken<CalendarData[]>(
    [
      `${endpoint}`,
      {
        username: email,
        from,
        to,
        messagesPerDay: density === 'compact' ? PAGE_SIZE_DEFAULT * COMPACT_MESSAGES_MODIFIER : PAGE_SIZE_DEFAULT,
        ...getDashboardParams(userId, Boolean(isWorkspaceMode)),
        ...getWorkspaceParams(userId, Boolean(isWorkspaceMode)),
        ...filterParams,
      },
    ],
    {
      refetchInterval: GetCalendar.interval,
      enabled: !!userId && !!date,
      cacheTime: GetCalendar.cacheTime,
      useErrorBoundary: true,
    }
  );
};

export const useInfiniteGetMessagesByDay = (date: CalendarMonthString, enabled: boolean) => {
  const [
    {
      user: { email, userId, isWorkspaceMode },
    },
  ] = useBridgeContext();
  const endpoint = getWorkspaceEndpoint(GetCalendar.endpoint, Boolean(isWorkspaceMode));
  const { filterParams } = useCalendarContext();

  const from = buildDateTime(date, DAY_INIT_TIME);
  const to = buildDateTime(date, DAY_END_TIME);

  return useInfiniteQueryWithAccessToken<CalendarData>(
    [
      endpoint,
      {
        username: email,
        from,
        to,
        pageSize: PAGE_SIZE_DEFAULT,
        perPage: PAGE_SIZE_DEFAULT,
        ...getDashboardParams(userId, Boolean(isWorkspaceMode)),
        ...getWorkspaceParams(userId, Boolean(isWorkspaceMode)),
        ...filterParams,
      },
    ],
    {
      refetchInterval: false,
      enabled: !!userId && !!date && enabled,
      cacheTime: GetCalendar.cacheTime,
      useErrorBoundary: true,
    }
  );
};

export const useInfiniteGetMessagesByRange = (init: CalendarMonthString, end: CalendarMonthString, enabled: boolean) => {
  const [
    {
      user: { email, userId, isWorkspaceMode },
    },
  ] = useBridgeContext();
  const endpoint = getWorkspaceEndpoint(GetCalendar.endpoint, Boolean(isWorkspaceMode));
  const { filterParams } = useCalendarContext();

  const from = buildDateTime(init, DAY_INIT_TIME);
  const to = buildDateTime(end, DAY_END_TIME);

  return useInfiniteQueryWithAccessToken<CalendarMessage[]>(
    [
      endpoint,
      {
        username: email,
        from,
        to,
        pageSize: PAGE_SIZE_MAX,
        perPage: PAGE_SIZE_MAX,
        ...getDashboardParams(userId, Boolean(isWorkspaceMode)),
        includeDeliveryRecipients: true,
        ...getWorkspaceParams(userId, Boolean(isWorkspaceMode)),
        ...filterParams,
      },
    ],
    {
      refetchInterval: false,
      enabled: !!userId && !!init && !!end && enabled,
      cacheTime: GetCalendar.cacheTime * 4,
      useErrorBoundary: true,
    }
  );
};

// A segment is a closed date range that is paged on its own. The daily view keeps an ordered list of
// them and only ever fetches from the two outermost ones, see DailyGrid/segments.ts.
export const SEGMENT_PAGE_SIZE = 50;

const SEGMENT_CACHE_TIME_MS = 2 * 60 * 1000;

// An open ended segment is one query that keeps paging, so it holds a sliding window of pages instead.
// Fetching a page at one end drops the page at the other end, and fetchPreviousPage brings dropped
// pages back. The page param is the page number, so it has to be read from the page params: the
// number of pages held no longer says which page is next.
export const OPEN_SEGMENT_MAX_PAGES = 6;

// What identifies a segment's query apart from the credentials the query wrapper adds. Shared by the query
// and by the code that trims its pages, so the two always agree on which cache entry is meant.
const useSegmentRequest = () => {
  const [
    {
      user: { email, userId, isWorkspaceMode },
    },
  ] = useBridgeContext();
  const endpoint = getWorkspaceEndpoint(GetCalendar.endpoint, Boolean(isWorkspaceMode));
  const { filterParams, density } = useCalendarContext();

  const paramsFor = (segment: CalendarSegment | undefined) => ({
    username: email,
    // An open end is left out of the request so the API only bounds the other side.
    ...(segment?.from ? { from: buildDateTime(segment.from, DAY_INIT_TIME) } : {}),
    ...(segment?.to ? { to: buildDateTime(segment.to, DAY_END_TIME) } : {}),
    // Segments that are scrolled towards the past load their most recent messages first, so the
    // page closest to the viewport is always the one that arrives first.
    sortDescending: segment?.direction === 'older',
    pageSize: SEGMENT_PAGE_SIZE,
    perPage: SEGMENT_PAGE_SIZE,
    ...getDashboardParams(userId, Boolean(isWorkspaceMode)),
    // Only the list density renders the per recipient failures. The compact card only needs the
    // delivery counts, which the API always returns. Changing the density changes the key and so
    // refetches, which is rare enough not to matter.
    includeDeliveryRecipients: density !== 'compact',
    ...getWorkspaceParams(userId, Boolean(isWorkspaceMode)),
    ...filterParams,
  });

  return { email, userId, endpoint, paramsFor };
};

export const useInfiniteGetMessagesBySegment = (segment: CalendarSegment | undefined, enabled: boolean) => {
  const { email, userId, endpoint, paramsFor } = useSegmentRequest();

  const isOpen = !!segment && (segment.from === null || segment.to === null);

  return useInfiniteQueryWithAccessToken<CalendarMessage[]>([endpoint, paramsFor(segment)], {
    refetchInterval: false,
    enabled: !!email && !!userId && !!segment && enabled,
    cacheTime: SEGMENT_CACHE_TIME_MS,
    useErrorBoundary: true,
    ...(isOpen && {
      maxPages: OPEN_SEGMENT_MAX_PAGES,
      // A page that is not a list of messages ends the list rather than paging for ever.
      getNextPageParam: (lastPage: CalendarMessage[], _allPages: CalendarMessage[][], lastPageParam: number) =>
        !Array.isArray(lastPage) || lastPage.length < SEGMENT_PAGE_SIZE ? undefined : lastPageParam + 1,
      getPreviousPageParam: (_firstPage: CalendarMessage[], _allPages: CalendarMessage[][], firstPageParam: number) =>
        firstPageParam > 1 ? firstPageParam - 1 : undefined,
    }),
  });
};

// Drops the pages of a closed segment beyond the first `keepPages`. They are the ones furthest from the focus
// date, so what stays is a run of dates that starts at the edge nearest the viewport. The segment then has
// another page to fetch, and scrolling back towards it fetches it again.
export const useTrimSegmentPages = () => {
  const queryClient = useQueryClient();
  const { endpoint, paramsFor } = useSegmentRequest();

  return (segment: CalendarSegment, keepPages: number) => {
    queryClient.setQueriesData<InfiniteData<CalendarMessage[], number>>({ queryKey: [endpoint, paramsFor(segment)] }, (data) =>
      data && data.pages.length > keepPages ? { pages: data.pages.slice(0, keepPages), pageParams: data.pageParams.slice(0, keepPages) } : data
    );
  };
};

export const useRescheduleMessage = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('calendar');
  const [{ user }] = useBridgeContext();
  const isWorkspaceMode = Boolean(user?.isWorkspaceMode);
  const endpoint = getWorkspaceEndpoint(RescheduleMessage.endpoint, isWorkspaceMode);
  const calendarSummaryEndpoint = getWorkspaceEndpoint(GetCalendarSummary.endpoint, isWorkspaceMode);
  const calendarMessageEndpoint = getWorkspaceEndpoint(GetCalendarMessage.endpoint, isWorkspaceMode);
  const calendarEndpoint = getWorkspaceEndpoint(GetCalendar.endpoint, isWorkspaceMode);
  const access_token = user.accessToken;
  const { method } = RescheduleMessage;
  const { showToastNotification } = useToastNotificationContext();

  return useMutation({
    mutationFn: async (payload: RescheduleMessageDataWithLocalized) => {
      const {
        message: { id },
        newSendDate,
        recursUntil,
      } = payload;

      const body = { newSendDate, recursUntil, ...getDashboardParams(user.userId, isWorkspaceMode) };
      const credentials = user.isSwitchedUser ? { username: user.username, logged_in_access_token: access_token } : { access_token };

      return fetchWithHeaders({
        method,
        url: `${endpoint}/${id}/reschedule`,
        params: { ...credentials, ...getWorkspaceParams(user.userId, isWorkspaceMode) },
        body,
      })
        .then((response: unknown) => (response as { result?: unknown }).result)
        .catch((error) => {
          reportErrorToFaro(error, 'CalendarModel useRescheduleMessage');

          throw error;
        });
    },
    onMutate: async (payload: RescheduleMessageDataWithLocalized) => {
      await queryClient.cancelQueries({ queryKey: [calendarSummaryEndpoint], exact: false });

      const matching = queryClient.getQueryCache().findAll({
        queryKey: [calendarSummaryEndpoint],
        exact: false,
      });

      const previous = matching.map((q) => ({
        key: q.queryKey,
        data: queryClient.getQueryData<unknown>(q.queryKey),
      }));

      matching.forEach((q) => {
        queryClient.setQueryData<CalendarSummaryDay[] | undefined>(q.queryKey, (old) => {
          if (!Array.isArray(old)) return old;

          return optimisticRescheduleSummary(old, payload);
        });
      });

      return { previous };
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (_err: any, _payload, context) => {
      if (context?.previous) {
        context.previous.forEach((p) => {
          queryClient.setQueryData(p.key, p.data);
        });
      }

      const errorCode = _err?.response?.data?.error?.code as string;
      const errorMessage = errorCode ? t(errorCode) : t('RescheduleGenericError');

      showToastNotification({
        hideTimeout: 1000,
        notificationText: errorMessage,
        type: NotificationTypeEnum.Error,
        showCloseButton: true,
      });

      window.pendo.track('[Publish] Calendar - Post drop failed');
    },
    onSuccess: () => {
      showToastNotification({
        hideTimeout: 1000,
        notificationText: t('RescheduleGenericSuccess') as string,
        type: NotificationTypeEnum.Success,
      });
      window.pendo.track('[Publish] Calendar - Post drop success');
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: [calendarSummaryEndpoint], exact: false });
      queryClient.invalidateQueries({ queryKey: [calendarMessageEndpoint], exact: false });
      if (variables?.message?.id) {
        queryClient.invalidateQueries({ queryKey: [`${calendarMessageEndpoint}/${variables.message.id}`], exact: false });
      }
      queryClient.invalidateQueries({ queryKey: [calendarEndpoint], exact: false });
    },
  });
};

export const useResolveRecipients = () => {
  const queryClient = useQueryClient();
  const [
    {
      user: { userId, accessToken, isWorkspaceMode },
    },
  ] = useBridgeContext();
  const endpoint = getWorkspaceEndpoint(ResolveRecipients.endpoint, Boolean(isWorkspaceMode));
  const calendarMessageEndpoint = getWorkspaceEndpoint(GetCalendarMessage.endpoint, Boolean(isWorkspaceMode));
  const calendarEndpoint = getWorkspaceEndpoint(endpoints.GetCalendar.endpoint, Boolean(isWorkspaceMode));
  const calendarSummaryEndpoint = getWorkspaceEndpoint(endpoints.GetCalendarSummary.endpoint, Boolean(isWorkspaceMode));

  const access_token = accessToken;
  const { method } = ResolveRecipients;

  return useMutation({
    mutationFn: async ({ recipientIds, messageId }: ResolveRecipientsArgs) =>
      fetchWithHeaders({
        method,
        url: `${endpoint}/${messageId}/resolve`,
        params: { userId, access_token, ...getWorkspaceParams(userId, Boolean(isWorkspaceMode)) },
        body: { recipientIds },
      }).then((response: unknown) => (response as { result?: unknown }).result),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [`${calendarMessageEndpoint}/${variables.messageId}`],
        exact: false,
        refetchType: 'active',
      });
      queryClient.invalidateQueries({ queryKey: [calendarEndpoint] });
      queryClient.invalidateQueries({ queryKey: [calendarSummaryEndpoint] });
    },
  });
};

export const useDeleteMessage = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('calendar');
  const [{ user }] = useBridgeContext();
  const isWorkspaceMode = Boolean(user?.isWorkspaceMode);
  const calendarSummaryEndpoint = getWorkspaceEndpoint(GetCalendarSummary.endpoint, isWorkspaceMode);
  const calendarMessageEndpoint = getWorkspaceEndpoint(GetCalendarMessage.endpoint, isWorkspaceMode);
  const calendarEndpoint = getWorkspaceEndpoint(GetCalendar.endpoint, isWorkspaceMode);
  const { showToastNotification } = useToastNotificationContext();
  const { closeSidebar } = useCalendarContext();
  const { removeFromSelection } = useSelectionContext();

  return useMutation({
    mutationFn: async (messageId: CalendarMessage['id']) =>
      fetchWithHeaders({
        method: 'GET',
        url: DeleteMessage.endpoint,
        params: {
          username: user.email,
          access_token: user.accessToken,
          method: DeleteMessage.method,
          application_id: 'whitelabel_global',
          message_id: messageId,
        },
      }),
    onSuccess: (_data, messageId) => {
      showToastNotification({
        hideTimeout: 3000,
        notificationText: t('delete_success') as string,
        type: NotificationTypeEnum.Success,
      });
      closeSidebar();
      // A post deleted through its own single-delete flow can still have been ticked in a
      // bulk selection — drop it so the bulk bar doesn't keep counting a post that no longer exists.
      removeFromSelection([messageId]);
    },
    onError: () => {
      showToastNotification({
        hideTimeout: 3000,
        notificationText: t('delete_error') as string,
        type: NotificationTypeEnum.Error,
        showCloseButton: true,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [calendarSummaryEndpoint], exact: false });
      queryClient.invalidateQueries({ queryKey: [calendarMessageEndpoint], exact: false });
      queryClient.invalidateQueries({ queryKey: [calendarEndpoint], exact: false });
    },
  });
};

type BulkDeleteResult = {
  failedIds: CalendarMessage['id'][];
  succeededIds: CalendarMessage['id'][];
};

export const useDeleteMultipleMessages = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('calendar');
  const [{ user }] = useBridgeContext();
  const isWorkspaceMode = Boolean(user?.isWorkspaceMode);
  const calendarSummaryEndpoint = getWorkspaceEndpoint(GetCalendarSummary.endpoint, isWorkspaceMode);
  const calendarMessageEndpoint = getWorkspaceEndpoint(GetCalendarMessage.endpoint, isWorkspaceMode);
  const calendarEndpoint = getWorkspaceEndpoint(GetCalendar.endpoint, isWorkspaceMode);
  const { showToastNotification } = useToastNotificationContext();

  return useMutation<BulkDeleteResult, Error, CalendarMessage['id'][]>({
    // "Select all" can now mean every post in a month, not just a handful — delete in
    // small concurrent batches rather than firing every request at once.
    mutationFn: async (messageIds: CalendarMessage['id'][]) => {
      const CONCURRENCY = 10;
      const succeededIds: CalendarMessage['id'][] = [];
      const failedIds: CalendarMessage['id'][] = [];

      for (let i = 0; i < messageIds.length; i += CONCURRENCY) {
        const batch = messageIds.slice(i, i + CONCURRENCY);
        const responses = await Promise.allSettled(
          batch.map((messageId) =>
            fetchWithHeaders({
              method: 'GET',
              url: DeleteMessage.endpoint,
              params: {
                username: user.email,
                access_token: user.accessToken,
                method: DeleteMessage.method,
                application_id: 'whitelabel_global',
                message_id: messageId,
              },
            })
          )
        );

        responses.forEach((response, index) => {
          if (response.status === 'fulfilled') succeededIds.push(batch[index]);
          else failedIds.push(batch[index]);
        });
      }

      if (failedIds.length > 0) {
        reportErrorToFaro(
          new Error(`Bulk delete: ${failedIds.length} of ${messageIds.length} messages failed`),
          'CalendarModel useDeleteMultipleMessages'
        );
      }

      // Only a total failure rejects the mutation (routing it to onError) — a partial failure
      // still resolves so the caller can invalidate queries and prune the ids that did succeed.
      if (succeededIds.length === 0 && failedIds.length > 0) throw new Error('Bulk delete failed');

      return { succeededIds, failedIds };
    },
    onSuccess: ({ succeededIds, failedIds }) => {
      if (failedIds.length > 0) {
        showToastNotification({
          hideTimeout: 5000,
          notificationText: t('delete_partial_bulk', {
            succeeded: succeededIds.length,
            total: succeededIds.length + failedIds.length,
            failed: failedIds.length,
          }) as string,
          type: NotificationTypeEnum.Error,
          showCloseButton: true,
        });
      } else {
        showToastNotification({
          hideTimeout: 3000,
          notificationText: t('delete_success_bulk', { count: succeededIds.length }) as string,
          type: NotificationTypeEnum.Success,
        });
      }
    },
    onError: () => {
      showToastNotification({
        hideTimeout: 3000,
        notificationText: t('delete_error_bulk') as string,
        type: NotificationTypeEnum.Error,
        showCloseButton: true,
      });
    },
    onSettled: () => {
      // Even a total failure may have partially applied server-side (e.g. some of the batch's
      // Promise.allSettled calls succeeded before the mutation as a whole was thrown) — always
      // refresh so any already-deleted posts disappear instead of sticking around stale.
      queryClient.invalidateQueries({ queryKey: [calendarSummaryEndpoint], exact: false });
      queryClient.invalidateQueries({ queryKey: [calendarMessageEndpoint], exact: false });
      queryClient.invalidateQueries({ queryKey: [calendarEndpoint], exact: false });
    },
  });
};
